import { useMutation } from '@apollo/client'
import { FunctionComponent, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Brand, BrandOnboardingStep } from '../../../gql/types'
import { useNotification } from '../../../utils/hooks'
import { UPDATE_BRAND } from '../../../gql'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import {
  Button,
  NotificationType,
  RadioGroup,
  TextField,
} from '../../../components'
import { Bullets } from '../components/Bullets'
import { Steps } from '../components/Steps'
import {
  sourceChannelLocalizedString,
  UserSourceChannels,
} from '../../../utils/GqlStrings'

type EditUserSourceChannelFormData = {
  sourceChannel: string
  otherSourceChannel: string
}

interface IDefaultProps {}
interface IProps extends Partial<IDefaultProps> {
  brand: Brand
}

const defaultProps: IDefaultProps = {}

const SetSourceChannel: FunctionComponent<IProps> = ({ brand }) => {
  const { addNotification } = useNotification()
  const [updateBrand, { loading: updateBrandLoading }] =
    useMutation(UPDATE_BRAND)

  const isOther = useMemo(() => {
    if (!brand.sourceChannel) {
      return false
    }

    if (UserSourceChannels.includes(brand.sourceChannel)) {
      return false
    }

    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.sourceChannel])

  const {
    handleSubmit,
    formState: { errors },
    watch,
    control,
  } = useForm({
    defaultValues: {
      sourceChannel: isOther ? 'other' : brand?.sourceChannel,
      otherSourceChannel: isOther ? brand?.sourceChannel : '',
    },
  })

  const isOtherSelected = useMemo(
    () => watch('sourceChannel') === 'other',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [watch('sourceChannel')],
  )

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewBrandsOnboardingSetSourceChannel)
  }, [])

  const onSubmit = async (form: EditUserSourceChannelFormData) => {
    try {
      await updateBrand({
        variables: {
          id: brand.id,
          input: {
            sourceChannel: isOtherSelected
              ? form.otherSourceChannel
              : form.sourceChannel,
            onboarding: BrandOnboardingStep.setWebsite,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  return (
    <div className='min-h-screen flex flex-col pb-14'>
      <div className='flex-grow'>
        <Steps
          brand={brand}
          stepCurrent={BrandOnboardingStep.setSourceChannel}
        />
        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='md:shadow rounded-t-md sm:overflow-hidden'>
                <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                  <div className='sm:mx-auto'>
                    <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
                      How did you find us?
                    </h2>
                    <p className='mt-2 md:text-center text-sm text-gray-600'>
                      Let us know how you discovered our product – it helps us
                      improve your experience!
                    </p>
                  </div>

                  <div className='grid gap-6'>
                    <div className='col-span-3 sm:col-span-2'>
                      <Controller
                        name='sourceChannel'
                        control={control}
                        rules={{ required: `Please select a source channel.` }}
                        render={({ field }) => (
                          <RadioGroup
                            options={UserSourceChannels.map(
                              (sourceChannel) => ({
                                type: sourceChannel,
                                name: sourceChannelLocalizedString(
                                  sourceChannel,
                                ),
                              }),
                            )}
                            field={field}
                            error={errors?.sourceChannel?.message}
                            className='grid grid-cols-1 sm:grid-cols-2'
                          />
                        )}
                      />

                      {isOtherSelected && (
                        <div className='mt-4'>
                          <Controller
                            name='otherSourceChannel'
                            control={control}
                            rules={{
                              required: 'Please specify how you heard about us',
                              maxLength: {
                                value: 255,
                                message:
                                  'Your response cannot be longer than 255 characters',
                              },
                            }}
                            render={({ field }) => (
                              <TextField
                                name='otherSourceChannel'
                                placeholder='Please specify how you heard about us'
                                label='Other source'
                                field={field}
                                error={errors.otherSourceChannel?.message}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className='md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
                <Button
                  title='Continue'
                  type='submit'
                  loading={updateBrandLoading}
                  disabled={updateBrandLoading}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={BrandOnboardingStep.setSourceChannel} />
    </div>
  )
}

SetSourceChannel.defaultProps = defaultProps

export { SetSourceChannel }
